// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import React, { useId } from 'react';

interface ProviderAccessRejectedIllustrationProps {
  /** Square size in px (width and height). Defaults to 100. */
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  'aria-label'?: string;
}

/**
 * "Submission rejected" illustration — a 100×100 red X-in-circle shown on
 * the Change of Agency rejection screen. Square aspect; scales cleanly at
 * any size via the {@link size} prop.
 */
export const ProviderAccessRejectedIllustration: React.FC<
  ProviderAccessRejectedIllustrationProps
> = ({
  size = 100,
  className,
  style,
  'aria-label': ariaLabel = 'Submission rejected',
}) => {
  const id = useId();
  const clipId = `pa-rejected-clip-${id}`;
  const maskId = `pa-rejected-mask-${id}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
      className={className}
      style={style}
    >
      <rect width="100" height="100" rx="50" fill="#96040E" />
      <g clipPath={`url(#${clipId})`}>
        <mask
          id={maskId}
          style={{ maskType: 'luminance' }}
          maskUnits="userSpaceOnUse"
          x="22"
          y="22"
          width="56"
          height="56"
        >
          <path d="M78 22H22V78H78V22Z" fill="white" />
        </mask>
        <g mask={`url(#${maskId})`}>
          <path d="M50 75.0833C36.1633 75.0833 24.9167 63.8367 24.9167 50C24.9167 36.1633 36.1633 24.9167 50 24.9167C63.8367 24.9167 75.0833 36.1633 75.0833 50C75.0833 63.8367 63.8367 75.0833 50 75.0833ZM50 28.4167C38.1 28.4167 28.4167 38.1 28.4167 50C28.4167 61.9 38.1 71.5833 50 71.5833C61.9 71.5833 71.5833 61.9 71.5833 50C71.5833 38.1 61.9 28.4167 50 28.4167Z" fill="white" />
          <path d="M41.65 59.5833C41.2067 59.5833 40.7633 59.42 40.4133 59.07C39.7367 58.3933 39.7367 57.2733 40.4133 56.5967L56.5967 40.4133C57.2733 39.7367 58.3933 39.7367 59.07 40.4133C59.7467 41.09 59.7467 42.21 59.07 42.8867L42.8867 59.07C42.56 59.42 42.0933 59.5833 41.65 59.5833Z" fill="white" />
          <path d="M57.8333 59.5833C57.39 59.5833 56.9467 59.42 56.5967 59.07L40.4133 42.8867C39.7367 42.21 39.7367 41.09 40.4133 40.4133C41.09 39.7367 42.21 39.7367 42.8867 40.4133L59.07 56.5967C59.7467 57.2733 59.7467 58.3933 59.07 59.07C58.72 59.42 58.2767 59.5833 57.8333 59.5833Z" fill="white" />
        </g>
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="56" height="56" fill="white" transform="translate(22 22)" />
        </clipPath>
      </defs>
    </svg>
  );
};
